/** 未同期の書き込みキュー（仕様書 §7.2）。フェーズ4で SyncEngine が消費する。 */

import { getDb, type OutboxOp } from './schema';
import { nowIsoUtc } from '../lib/datetime';
import { notifyOutboxChanged } from './events';

type NewOp = Pick<OutboxOp, 'type' | 'targetKey'> &
  Partial<Pick<OutboxOp, 'entryId' | 'month' | 'prevMonth' | 'dateKey' | 'week' | 'taskKind' | 'period'>>;

/**
 * コアレッシング: 同一 targetKey の pending が既にあれば新規追加せず、
 * createdAt を更新するだけにする。ペイロードは送信時にIndexedDBの現在値を読むため、
 * 常に最新が送られる。
 */
export async function enqueue(op: NewOp): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('outbox', 'readwrite');
  const idx = tx.store.index('by_target');
  const now = nowIsoUtc();

  let merged = false;
  for await (const cursor of idx.iterate(op.targetKey)) {
    const cur = cursor.value;
    if (cur.status === 'pending') {
      await cursor.update({
        ...cur,
        ...op,
        // 月移動の情報は失わない（最初の旧月を保持する）
        prevMonth: cur.prevMonth ?? op.prevMonth,
        createdAt: now,
        nextAttemptAt: now,
      });
      merged = true;
      break;
    }
  }

  if (!merged) {
    await tx.store.add({
      ...op,
      createdAt: now,
      attempts: 0,
      nextAttemptAt: now,
      status: 'pending',
    } as OutboxOp);
  }
  await tx.done;
  notifyOutboxChanged();
}

export async function pendingCount(): Promise<number> {
  const db = await getDb();
  return db.countFromIndex('outbox', 'by_status', 'pending');
}

export async function listOps(): Promise<OutboxOp[]> {
  const db = await getDb();
  return db.getAll('outbox');
}

export async function clearOutbox(): Promise<void> {
  const db = await getDb();
  await db.clear('outbox');
}
